import React, { useEffect, useState } from "react";
import { useTheme, withStyles } from "@material-ui/core/styles";
import { isVotePassed, toFixed, tokens } from "../../../constants/constants";
import { getTimeConversion } from "../../../utils/getTimeConversion";
import moment from "moment";
import bigInt from "big-integer";

const styles = (theme) => ({
  poolStatusBoxLg: {
    minWidth: "105px",
    width: "fit-content",
    padding: "2px 13px",
    borderRadius: "24px",
    textAlign: "center",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: theme?.colors.poolCard?.poolTypeText,
    whiteSpace: "nowrap",

    fontSize: "13px",
    [theme.breakpoints.up("sm")]: {
      minWidth: "140px",
      fontSize: "15px",
    },
  },
  poolStatusBoxSm: {
    minWidth: "80px",
    width: "fit-content",
    padding: "1px 12px",
    borderRadius: "24px",
    textAlign: "center",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: theme?.colors.poolCard?.poolTypeText,
    whiteSpace: "nowrap",

    fontSize: "11px",
    [theme.breakpoints.up("sm")]: {
      fontSize: "12px",
    },
  },
});

export const getPoolType = (poolInfo, theme) => {
  const fundingTokenDecimals =
    tokens[poolInfo?.fundingTokenAddress]?.decimals ?? 18;

  const isCancelled = poolInfo?.presaleCancelled;
  const isStarted = moment.unix(poolInfo.openTime).isBefore(moment.now());
  const isEnded = moment.unix(poolInfo.closeTime).isBefore(moment.now());

  const totalCollected = parseFloat(
    toFixed(bigInt(poolInfo?.totalCollectedWei || "0"), fundingTokenDecimals, 3)
  );
  const hardCap = parseFloat(
    toFixed(bigInt(poolInfo?.hardCapInWei || "0"), fundingTokenDecimals, 3)
  );
  const softCap = parseFloat(
    toFixed(bigInt(poolInfo?.softCapInWei || "0"), fundingTokenDecimals, 3)
  );
  const votePassed = isVotePassed(poolInfo);

  if (isCancelled) return ["CANCELLED"];
  if (totalCollected >= hardCap) return ["FILLED"];
  if (isEnded) {
    if (totalCollected < softCap) {
      return ["EXPIRED"];
    }
    return ["COMPLETED"];
  }

  if (!isStarted) {
    const currentTimestamp = Math.floor(Date.now() / 1000);
    const remainingSeconds = Number(poolInfo?.openTime) - currentTimestamp;
    const futureSeconds = Math.abs(remainingSeconds);

    return ["NOTOPENED", getTimeConversion(futureSeconds)];
  }

  if (!votePassed && !poolInfo?.certified && poolInfo?.presaleType !== "2")
    return ["NOTPASSED"];

  const currentTimestamp = Math.floor(Date.now() / 1000);
  const remainingSeconds = Number(poolInfo?.closeTime) - currentTimestamp;

  return ["ONGOING", getTimeConversion(remainingSeconds)];
};

export const getStatusBarColor = (poolInfo, theme, poolType) => {
  if (!poolType) {
    poolType = getPoolType(poolInfo, theme)[0];
  }

  if (poolType === "FILLED") return "GREEN";
  if (poolType === "COMPLETED") return "DARK_GREEN";
  if (poolType === "NOTPASSED") return "RED";
  if (poolType === "NOTOPENED") return "YELLOW";
  if (poolType === "ONGOING") return "YELLOW";
  if (poolType === "EXPIRED") return "RED";
  return "RED";
};

export const getStatusText = (poolInfo, theme, poolType, timeRemaining) => {
  if (!poolType) {
    poolType = getPoolType(poolInfo, theme)[0];
  }

  switch (poolType) {
    case "COMPLETED":
      return "completed";
    case "CANCELLED":
      return "cancelled";
    case "NOTPASSED":
      return "not passed";
    case "EXPIRED":
      return "expired";
    case "FILLED":
      return "filled";
    case "ONGOING":
      return "ending in " + timeRemaining;
    case "NOTOPENED":
      return "starting in " + timeRemaining;
    default:
      return ""; // Default case if none of the above matches
  }
};

const Status = (props) => {
  const { classes, poolInfo, size = "lg" } = props;

  const theme = useTheme();

  const [backgroundColor, setBackgroundColor] = useState("transparent");
  const [text, setText] = useState("transparent");

  useEffect(() => {
    const [poolType, timeRemaining] = getPoolType(poolInfo, theme);

    const fillBarColorId = getStatusBarColor(poolInfo, theme, poolType);

    const statusText = getStatusText(poolInfo, theme, poolType, timeRemaining);

    setBackgroundColor(theme?.colors.poolCard?.statusBarFilled[fillBarColorId]);
    setText(statusText);
  }, [poolInfo, theme]);

  return (
    <div
      className={
        size === "lg" ? classes.poolStatusBoxLg : classes.poolStatusBoxSm
      }
      style={{
        color: "white",
        backgroundColor: backgroundColor,
      }}
    >
      {text}
    </div>
  );
};

export default withStyles(styles)(Status);
